import { useCallback } from 'react'

import {
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { useUpdatePinnedAppsOrderMutation } from 'shared/api/containerApi/requests'
import { AppGroup } from 'shared/constants/apps'
import { useTopDockStore } from 'shared/stores/topDockStore'
import { AppConfig } from 'shared/types/app'

interface UseSortableAppsProps {
  appsByGroup: Array<{ [K in AppGroup]?: AppConfig[] }>
}

export const useSortableApps = ({ appsByGroup }: UseSortableAppsProps) => {
  const setPinnedAppIds = useTopDockStore(state => state.setPinnedAppIds)
  const updatePinnedAppsOrderMutation = useUpdatePinnedAppsOrderMutation()

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5
      }
    })
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent, group: AppGroup, groupApps: AppConfig[]) => {
      const { active, over } = event

      if (!over || active.id === over.id) {
        return
      }

      const oldIndex = groupApps.findIndex(app => app.id === active.id)
      const newIndex = groupApps.findIndex(app => app.id === over.id)

      if (oldIndex === -1 || newIndex === -1) return

      const reorderedGroupApps = arrayMove(groupApps, oldIndex, newIndex)

      const updatedAppsByGroup = appsByGroup.map(appGroup => {
        const groupKey = Object.keys(appGroup)[0] as AppGroup
        if (groupKey === group) {
          return { [group]: reorderedGroupApps }
        }
        return appGroup
      })

      const allAppIds = updatedAppsByGroup.flatMap(
        appGroup => Object.values(appGroup)?.[0]?.map(app => app.id) || []
      )

      setPinnedAppIds(allAppIds)
      updatePinnedAppsOrderMutation.mutate(allAppIds)
    },
    [appsByGroup, setPinnedAppIds, updatePinnedAppsOrderMutation]
  )

  return {
    sensors,
    handleDragEnd
  }
}
